<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'RegisterEntry',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
    },
    registerId: {
      type: String,
      required: false,
    },
    versionId: {
      type: String,
      required: false,
    },
    invoice: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getContainer = (...args) => {
    const [editor] = args;

    return {
      self: $fn.value.urlFor('create_register_entry', {
        register_id: editor.version.register_id,
        version_id: editor.version.version_id,
      }),
      object: 'list',
      data: [],
    };
  };
  const getPayload = (...args) => {
    const [editor] = args;
    const exclude =
      editor.document_type === 'invoice'
        ? ['contract', 'version']
        : editor.document_type === 'contract'
          ? ['invoice', 'version']
          : ['contract', 'invoice', 'version'];

    const result = editor.isNew
      ? editor.getData({ exclude })
      : editor.getChanges({ exclude, skipEmbeddedLists: true });

    if (result.invoice && !result.invoice.invoice_number) {
      result.invoice = null;
    }

    if (result.contract && !result.contract.contract_number) {
      result.contract = null;
    }

    return result;
  };
  const onDocumentTypeChanged = (...args) => {
    const [editor] = args;
    const docType = editor.document_type;

    if (editor.isNew) {
      return reloadTemplate(editor);
    }

    if (docType == 'invoice' && !editor.invoice) {
      editor.invoice = { id: null, invoice_number: null };
      return;
    }

    if (docType == 'contract' && !editor.contract) {
      editor.contract == { id: null, contract_number: null };
      return;
    }

    editor.invoice = null;
    editor.contract = null;
  };
  const reloadTemplate = (...args) => {
    const [editor] = args;

    return $fn.value
      .fetch('get_register_entry_template', {
        parameters: {
          entry_id: editor.id,
          register_id: editor.version.register_id,
          version_id: editor.version.version_id,
          direction: editor.direction,
          document_type: editor.document_type,
          invoice_id: editor.invoice?.id,
          contract_id: editor.contract?.id,
        },
        asResource: true,
      })
      .then((resource) => {
        setData('resource', resource);
        setData('editor', resource.editor());
      });
  };
  const createNew = (...args) => {
    return $fn.value
      .fetch('get_register_entry_template', {
        parameters: {
          register_id: resource.value.version.register_id,
          version_id: resource.value.version.version_id,
          direction: resource.value.direction,
          document_type: resource.value.document_type,
        },
        asResource: true,
      })
      .then((resource) => {
        setData('resource', resource);
        setData('editor', resource.editor());
      });
  };
  const onRegisterChanged = (...args) => {
    const [value] = args;
    editor.value.version.register_id = value.selectedRegisterId;
    editor.value.version.version_id = value.selectedVersionId;
    reloadTemplate(editor.value);
  };

  const formId = ref(null);
  const formName = ref('registerEntry');
  const pendingReload = ref(false);
  const createTitle = ref(
    $fn.value.pgettext('RegisterEntry', 'New Registry Entry')
  );
  const defaultTitle = ref($fn.value.pgettext('RegisterEntry', 'Edit Entry'));
  const registerObj = ref(null);
  const resource = ref(null);
  const editor = ref(null);
  const selectRegister = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    formName: formName.value,
    pendingReload: pendingReload.value,
    createTitle: createTitle.value,
    defaultTitle: defaultTitle.value,
    registerObj: registerObj.value,
    resource: resource.value,
    editor: editor.value,
    selectRegister: selectRegister.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_registerObj = () => {
    return null;
  };
  const $$load_resource = () => {
    return props.oid
      ? $fn.value.fetch('read_register_entry', {
          parameters: { id: props.oid },
          asResource: true,
        })
      : $fn.value.fetch('get_register_entry_template', {
          parameters: {
            register_id: props.registerId,
            version_id: props.versionId,
            invoice_id: props.invoice ? props.invoice.id : null,
          },
          asResource: true,
        });
  };
  const $$load_editor = () => {
    return resource.value.editor();
  };
  const $$load_selectRegister = () => {
    return (
      resource.value &&
      resource.value.isNew &&
      props.invoice &&
      !props.registerId &&
      !props.versionId
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'formName' },
      { name: 'pendingReload' },
      { name: 'createTitle' },
      { name: 'defaultTitle' },
      { name: 'registerObj' },
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'selectRegister', dependsOn: ['resource'] },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      registerObj: $$load_registerObj,
      resource: $$load_resource,
      editor: $$load_editor,
      selectRegister: $$load_selectRegister,
    },
    ux: {},
    dataRefs: {
      formId,
      formName,
      pendingReload,
      createTitle,
      defaultTitle,
      registerObj,
      resource,
      editor,
      selectRegister,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isDeletable = computed(() => {
    return props.oid != null;
  });
  const creatingTitle = computed(() => {
    return $fn.value.pgettext('RegisterEntry', 'Create new entry');
  });
  const editingTitle = computed(() => {
    if (loaded.value) {
      return '#' + resource.value.entry_number;
    }
    return null;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: resource.value.isNew ? creatingTitle.value : editingTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.registerId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('registerId');
    }
  );
  watch(
    () => props.versionId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('versionId');
    }
  );
  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_4ia5ryvt9_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
        ok: pendingReload.value,
      },
    };
  };
  const act_8564662037462133_closeComponent_prft2yutx_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: pendingReload.value,
      },
    };
  };
  const act_6466705138206980_closeComponent_59gqmd59x_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: true,
      },
    };
  };
  const act_6466705138206980_reloadGrid_u9meg57gv_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_6466705138206980_script_m0w4lufjo_value_callable = async (
    $event
  ) => {
    return onRegisterChanged($event.data);
  };
  const act_6466705138206980_script_m0w4lufjo_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6466705138206980_script_m0w4lufjo_value_callable($event);
      },
    };
  };
  const act_1040167445267876_emit_2k4vc066m_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data,
    };
  };
  const act_1040167445267876_reloadGrid_4xeriw0wt_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_1040167445267876_setData_vam7la7ky_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectRegister',
      value: false,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_setData_oxuobrexd_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'pendingReload',
      value: true,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_rest_lhp8iv3d8_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: getContainer(editor.value),
      resource: editor.value,
      data: getPayload(editor.value),
    };
  };
  const act_5534025912102772_script_8pgm5bkug_value_callable = async (
    $event
  ) => {
    return onDocumentTypeChanged(editor.value);
  };
  const act_5534025912102772_script_8pgm5bkug_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5534025912102772_script_8pgm5bkug_value_callable($event);
      },
    };
  };
  const act_5534025912102772_emit_ws9par1d0_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data,
    };
  };
  const act_2140198529698299_script_lo73hgwk6_value_callable = async (
    $event
  ) => {
    return createNew();
  };
  const act_2140198529698299_script_lo73hgwk6_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2140198529698299_script_lo73hgwk6_value_callable($event);
      },
    };
  };
  const act_2076709591253261_closeComponent_qea02cxuj_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: pendingReload.value,
      },
    };
  };

  const act_7315092382398562_closeModal_4ia5ryvt9 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_4ia5ryvt9_getActionArgs,
    });
  const inst_evh_7315092382398562_close_r4aac83uc =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_4ia5ryvt9,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_r4aac83uc],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_ha5kypsx4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_x975e09o6 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ha5kypsx4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_x975e09o6],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_prft2yutx =
    new actionTypes.CloseComponentAction({
      actionArgs: act_8564662037462133_closeComponent_prft2yutx_getActionArgs,
    });
  const inst_evh_8564662037462133_close_up9ugwpaf =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_prft2yutx,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_up9ugwpaf],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_w1rd14d26 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_wob4qya3b =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_w1rd14d26,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_wob4qya3b],
    event: 'reload',
  });
  const act_6466705138206980_closeComponent_59gqmd59x =
    new actionTypes.CloseComponentAction({
      actionArgs: act_6466705138206980_closeComponent_59gqmd59x_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_lkof7h1bz =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeComponent_59gqmd59x,
      event: 'deleted',
      displayName: 'closeComponent',
    });
  const act_6466705138206980_reloadGrid_u9meg57gv =
    new actionTypes.ReloadGridAction({
      actionArgs: act_6466705138206980_reloadGrid_u9meg57gv_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_ehh915j9d =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadGrid_u9meg57gv,
      event: 'deleted',
      displayName: 'reloadGrid',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_6466705138206980_deleted_lkof7h1bz,
      inst_evh_6466705138206980_deleted_ehh915j9d,
    ],
    event: 'deleted',
  });
  const act_6466705138206980_script_m0w4lufjo = new actionTypes.ScriptAction({
    actionArgs: act_6466705138206980_script_m0w4lufjo_getActionArgs,
  });
  const inst_evh_6466705138206980_registerChanged_cqc9jwnlh =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_script_m0w4lufjo,
      event: 'registerChanged',
      displayName: 'script',
    });
  const inst_evh_6466705138206980_registerChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6466705138206980_registerChanged_cqc9jwnlh],
      event: 'registerChanged',
    });
  const act_1040167445267876_emit_2k4vc066m = new actionTypes.EmitAction({
    actionArgs: act_1040167445267876_emit_2k4vc066m_getActionArgs,
  });
  const inst_evh_1040167445267876_saved_rcfai3bik =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_emit_2k4vc066m,
      event: 'saved',
      displayName: 'emit',
    });
  const act_1040167445267876_reloadGrid_4xeriw0wt =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_4xeriw0wt_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_szthlqkc6 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_4xeriw0wt,
      event: 'saved',
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_setData_vam7la7ky = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_vam7la7ky_getActionArgs,
  });
  const inst_evh_1040167445267876_saved_3dhms49t2 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_vam7la7ky,
      event: 'saved',
      displayName: 'setData',
    });
  const act_1040167445267876_setData_oxuobrexd = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_oxuobrexd_getActionArgs,
  });
  const inst_evh_1040167445267876_saved_vs4n1fda6 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_oxuobrexd,
      event: 'saved',
      displayName: 'setData',
    });
  const act_1040167445267876_rest_lhp8iv3d8 = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_lhp8iv3d8_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_1040167445267876_saved_rcfai3bik,
      inst_evh_1040167445267876_saved_szthlqkc6,
      inst_evh_1040167445267876_saved_3dhms49t2,
      inst_evh_1040167445267876_saved_vs4n1fda6,
    ],
  });
  const inst_evh_1040167445267876_submit_xkp5tkm2z =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_lhp8iv3d8,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_xkp5tkm2z],
    event: 'submit',
  });
  const act_5534025912102772_script_8pgm5bkug = new actionTypes.ScriptAction({
    actionArgs: act_5534025912102772_script_8pgm5bkug_getActionArgs,
  });
  const inst_evh_5534025912102772_documentTypeChanged_b0su8ktgh =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_script_8pgm5bkug,
      event: 'documentTypeChanged',
      displayName: 'script',
    });
  const inst_evh_5534025912102772_documentTypeChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5534025912102772_documentTypeChanged_b0su8ktgh],
      event: 'documentTypeChanged',
    });
  const act_5534025912102772_reloadComponentData_53fnqe3o9 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5534025912102772_reload_o9oxpkwk1 =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_reloadComponentData_53fnqe3o9,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5534025912102772_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_reload_o9oxpkwk1],
    event: 'reload',
  });
  const act_5534025912102772_closeComponent_3kf42wcco =
    new actionTypes.CloseComponentAction({});
  const inst_evh_5534025912102772_close_xjw6oxzn1 =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_closeComponent_3kf42wcco,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_5534025912102772_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_close_xjw6oxzn1],
    event: 'close',
  });
  const act_5534025912102772_emit_ws9par1d0 = new actionTypes.EmitAction({
    actionArgs: act_5534025912102772_emit_ws9par1d0_getActionArgs,
  });
  const inst_evh_5534025912102772_changed_mxl2uj3ue =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_emit_ws9par1d0,
      event: 'changed',
      displayName: 'emit',
    });
  const inst_evh_5534025912102772_changed = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_changed_mxl2uj3ue],
    event: 'changed',
  });
  const act_2140198529698299_script_lo73hgwk6 = new actionTypes.ScriptAction({
    actionArgs: act_2140198529698299_script_lo73hgwk6_getActionArgs,
  });
  const inst_evh_2140198529698299_click_awr4pdt7z =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_script_lo73hgwk6,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_2140198529698299_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2140198529698299_click_awr4pdt7z],
    event: 'click',
  });
  const act_2076709591253261_closeComponent_qea02cxuj =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2076709591253261_closeComponent_qea02cxuj_getActionArgs,
    });
  const inst_evh_2076709591253261_clickPreventStop_7vhacgyyk =
    new actionTypes.EventHandlerImpl({
      action: act_2076709591253261_closeComponent_qea02cxuj,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2076709591253261_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2076709591253261_clickPreventStop_7vhacgyyk],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_r4aac83uc,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_x975e09o6,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_up9ugwpaf,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_wob4qya3b,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_lkof7h1bz,
    inst_evh_6466705138206980_deleted_ehh915j9d,
    inst_evh_6466705138206980_deleted,
    inst_evh_6466705138206980_registerChanged_cqc9jwnlh,
    inst_evh_6466705138206980_registerChanged,
    inst_evh_1040167445267876_saved_rcfai3bik,
    inst_evh_1040167445267876_saved_szthlqkc6,
    inst_evh_1040167445267876_saved_3dhms49t2,
    inst_evh_1040167445267876_saved_vs4n1fda6,
    inst_evh_1040167445267876_submit_xkp5tkm2z,
    save: inst_evh_1040167445267876_submit_xkp5tkm2z,
    inst_evh_1040167445267876_submit,
    inst_evh_5534025912102772_documentTypeChanged_b0su8ktgh,
    inst_evh_5534025912102772_documentTypeChanged,
    inst_evh_5534025912102772_reload_o9oxpkwk1,
    inst_evh_5534025912102772_reload,
    inst_evh_5534025912102772_close_xjw6oxzn1,
    inst_evh_5534025912102772_close,
    inst_evh_5534025912102772_changed_mxl2uj3ue,
    inst_evh_5534025912102772_changed,
    inst_evh_2140198529698299_click_awr4pdt7z,
    inst_evh_2140198529698299_click,
    inst_evh_2076709591253261_clickPreventStop_7vhacgyyk,
    inst_evh_2076709591253261_clickPreventStop,
  };

  const evh_7315092382398562_close_r4aac83uc = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_r4aac83uc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_x975e09o6 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_x975e09o6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_up9ugwpaf = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_up9ugwpaf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_wob4qya3b = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_wob4qya3b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_lkof7h1bz = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_lkof7h1bz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_ehh915j9d = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_ehh915j9d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_registerChanged_cqc9jwnlh = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_registerChanged_cqc9jwnlh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_registerChanged = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_registerChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_rcfai3bik = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_rcfai3bik.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_szthlqkc6 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_szthlqkc6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_3dhms49t2 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_3dhms49t2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_vs4n1fda6 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_vs4n1fda6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_xkp5tkm2z = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_xkp5tkm2z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_documentTypeChanged_b0su8ktgh = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5534025912102772_documentTypeChanged_b0su8ktgh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_documentTypeChanged = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_documentTypeChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload_o9oxpkwk1 = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload_o9oxpkwk1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_close_xjw6oxzn1 = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_close_xjw6oxzn1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_close = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_changed_mxl2uj3ue = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_changed_mxl2uj3ue.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_changed = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_click_awr4pdt7z = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_click_awr4pdt7z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_click = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2076709591253261_clickPreventStop_7vhacgyyk = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2076709591253261_clickPreventStop_7vhacgyyk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2076709591253261_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2076709591253261_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="RegisterEntry"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.register.color"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <UcRegisterEntryHeader
          :entry="resource"
          :selectRegister="selectRegister"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
          @registerChanged="evh_6466705138206980_registerChanged($event, {})"
        >
        </UcRegisterEntryHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        :readonly="resource.isReadOnly"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetRegisterEntry
          :item="editor"
          :form="forms[formName]"
          @documentTypeChanged="
            evh_5534025912102772_documentTypeChanged($event, {})
          "
          @reload="evh_5534025912102772_reload($event, {})"
          @close="evh_5534025912102772_close($event, {})"
          @changed="evh_5534025912102772_changed($event, {})"
        >
        </FieldsetRegisterEntry>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter
        :card="!modal"
        :modal="modal"
        :color="$config.register.color"
        v-if="loaded"
      >
        <ItpButton
          :text="pgettext('RegisterEntry|Command', 'Create another entry')"
          :disabled="
            !forms[formName] ||
            !forms[formName].submittable ||
            actions.save.isRunning
          "
          variant="light"
          iconAppend="mdi:forward"
          v-if="!resource.isNew"
          @click="evh_2140198529698299_click($event, {})"
        >
        </ItpButton>
        <ItpBox class="ms-auto">
          <ItpButton
            :form="forms[formName]"
            :text="pgettext('RegisterEntry/Button', 'Create entry')"
            type="submit"
            :spinning="actions.save.isRunning"
            class="me-2"
            v-if="resource.isNew"
          >
          </ItpButton>
          <ItpButton
            :form="formId"
            :text="pgettext('Button', 'Save changes')"
            type="submit"
            :spinning="actions.save.isRunning"
            class="me-2"
            v-if="!resource.isNew && !resource.isReadOnly && forms[formName]"
          >
          </ItpButton>
          <ItpButton
            :text="gettext('Close')"
            variant="default"
            @click.prevent.stop="
              evh_2076709591253261_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </UcFooter>
    </template>
  </ItpPage>
</template>
